/**
 * Detection and backend-reporting of the installed Navigator Auto-Fill userscript,
 * which injects <meta name="navigator-autofill-version" content="X"> on the Navigator
 * site (see scripts/autofill/navigator-site.js). Tampermonkey may inject it after
 * React mounts, so detection polls briefly.
 */

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { reportPluginVersion } from '../store/userSlice';

const MARKER = 'navigator-autofill-version';

// Latest published userscript version — keep in sync with the script's @version
// (scripts/autofill/constants.js). The install host is behind Midway with no CORS,
// so the browser can't read the published version at runtime.
export const LATEST_AUTOFILL_VERSION = '1.6.3';

/** Compare dotted version strings: >0 if a is newer than b, 0 if equal, <0 otherwise. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function readMarker(): string | null {
  const marker = document.querySelector(`meta[name="${MARKER}"]`);
  return marker ? marker.getAttribute('content') || '' : null;
}

/**
 * Poll for the marker, resolving with the version as soon as it appears, or null
 * if still absent after the polling window (treat as not installed).
 */
export function detectAutofillVersionAsync(attempts = 10, intervalMs = 500): Promise<string | null> {
  return new Promise((resolve) => {
    if (readMarker() !== null) return resolve(readMarker());
    let n = 0;
    const id = setInterval(() => {
      const v = readMarker();
      if (v !== null || ++n >= attempts) {
        clearInterval(id);
        resolve(v);
      }
    }, intervalMs);
  });
}

/**
 * Once user metadata is fetched, report the installed autofill version to the backend
 * when it differs from the stored value — capturing install, upgrade, and uninstall ('')
 * transitions. Best-effort telemetry; runs once per metadata load.
 */
export function useReportPluginVersion(): void {
  const dispatch = useAppDispatch();
  const metadataStatus = useAppSelector((s) => s.user.metadataStatus);
  const stored = useAppSelector((s) => s.user.pluginVersion);

  useEffect(() => {
    if (metadataStatus !== 'fulfilled') return;
    let cancelled = false;
    detectAutofillVersionAsync().then((detected) => {
      const installed = detected ?? ''; // null (absent) => uninstalled
      if (!cancelled && installed !== stored) {
        dispatch(reportPluginVersion(installed));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, metadataStatus, stored]);
}
